/**
 * Site header and cart drawer from the Qwik build.
 *
 * The header is the blue-to-indigo gradient bar with the light (or admin
 * navy) strip carrying the signed-in name, My Cart and Logout. The cart
 * drawer lists campaigns with `in_cart_at` set: there is no Vendure active
 * order any more, an order is created at checkout and paying for it takes
 * the campaign out of the cart.
 */

import { SUPPORT_PHONE } from "@shared/campaign-rules.ts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import {
  createContext,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CampaignOutlineIcon,
  CloseIcon,
  LogoutIcon,
  ShoppingBagIcon,
  UserIcon,
} from "../../icons/index.tsx";
import {
  api,
  type Campaign,
  type PriceTierRow,
  type Product,
  type SessionUser,
} from "../../lib/api.ts";
import { signOut } from "../../lib/auth-client.ts";
import { formatNumbers, formatPrices } from "../../lib/legacy.ts";
import { EmptyState } from "../account-ui.tsx";
import { UserMenu } from "../UserMenu.tsx";

export interface CampaignHeader {
  name: string;
  onOpen: () => void;
}

export interface ViewingCustomer {
  name: string;
}

interface ShellState {
  showCart: boolean;
  setShowCart: (v: boolean) => void;
  adminMode: boolean;
  setAdminMode: (v: boolean) => void;
  campaignHeader: CampaignHeader | null;
  setCampaignHeader: (v: CampaignHeader | null) => void;
  viewingCustomer: ViewingCustomer | null;
  setViewingCustomer: (v: ViewingCustomer | null) => void;
  lockNotice: string | null;
  setLockNotice: (v: string | null) => void;
}

const ShellContext = createContext<ShellState>({
  showCart: false,
  setShowCart: () => {},
  adminMode: false,
  setAdminMode: () => {},
  campaignHeader: null,
  setCampaignHeader: () => {},
  viewingCustomer: null,
  setViewingCustomer: () => {},
  lockNotice: null,
  setLockNotice: () => {},
});

export const useShell = () => useContext(ShellContext);

/** The support number in a lock message becomes a tel link. */
function withPhoneLink(message: string) {
  const at = message.indexOf(SUPPORT_PHONE);
  if (at < 0) return message;
  return (
    <>
      {message.slice(0, at)}
      <a href={`tel:${SUPPORT_PHONE.replaceAll(" ", "")}`} className="font-semibold underline">
        {SUPPORT_PHONE}
      </a>
      {message.slice(at + SUPPORT_PHONE.length)}
    </>
  );
}

export function ShellProvider({ children }: { children: ReactNode }) {
  const [showCart, setShowCart] = useState(false);
  const [adminMode, setAdminMode] = useState(false);
  const [campaignHeader, setCampaignHeader] = useState<CampaignHeader | null>(null);
  const [viewingCustomer, setViewingCustomer] = useState<ViewingCustomer | null>(null);
  const [lockNotice, setLockNotice] = useState<string | null>(null);
  const value = useMemo(
    () => ({
      showCart,
      setShowCart,
      adminMode,
      setAdminMode,
      campaignHeader,
      setCampaignHeader,
      viewingCustomer,
      setViewingCustomer,
      lockNotice,
      setLockNotice,
    }),
    [showCart, adminMode, campaignHeader, viewingCustomer, lockNotice],
  );
  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

/**
 * Campaigns the user added to the cart and has not paid for. Under the
 * "campaigns" key so every campaign edit refreshes it.
 */
export function useCartCampaigns(user: SessionUser | null) {
  return useQuery({
    queryKey: ["campaigns", "cart", user?.id],
    enabled: Boolean(user),
    queryFn: async () => {
      const { campaigns } = await api.get<{ campaigns: Campaign[] }>("/campaigns");
      return campaigns.filter(
        (c) =>
          c.in_cart_at &&
          c.status !== "cancelled" &&
          c.status !== "deleted" &&
          (user?.role !== "customer" || (c.customer_id ?? c.user_id) === user.id),
      );
    },
  });
}

export function Header({ user }: { user: SessionUser | null }) {
  const { showCart, setShowCart, adminMode, campaignHeader, viewingCustomer, lockNotice } =
    useShell();
  const { pathname } = useLocation();
  const cart = useCartCampaigns(user);
  const totalQuantity = cart.data?.length ?? 0;
  const catalog = pathname === "/";
  const bar = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = bar.current;
    if (!el) return;
    const publish = () =>
      document.documentElement.style.setProperty(
        "--header-h",
        `${el.getBoundingClientRect().height}px`,
      );
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--header-h");
    };
  }, []);

  return (
    <div ref={bar} className="sticky top-0 z-10">
      <header className="bg-gradient-to-r from-blue-700 to-indigo-900 shadow-xl">
        <div
          className={`${adminMode ? "bg-admin-dm" : "bg-light-dm"} text-white shadow-inner text-center text-sm py-1`}
        >
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="mx-auto flex min-h-8 max-w-7xl items-center justify-between my-1">
              <div className="flex justify-between items-center w-full gap-4">
                <div className="min-w-0 flex-1 text-left">
                  {campaignHeader ? (
                    <div className="flex min-w-0 items-center gap-2.5">
                      <CampaignOutlineIcon className="h-[2.4rem] w-[2.4rem] shrink-0 text-white" />
                      <button
                        type="button"
                        onClick={campaignHeader.onOpen}
                        className="block min-w-0 max-w-full truncate text-left text-base font-semibold leading-tight text-white hover:underline sm:text-lg"
                      >
                        {campaignHeader.name}
                      </button>
                    </div>
                  ) : (
                    catalog && (
                      <>
                        <h1 className="text-base font-semibold leading-tight text-white sm:text-lg">
                          Choose your mail product
                        </h1>
                        <p className="mt-0.5 text-xs leading-snug text-white/80">
                          Pick a format to start a campaign. Pricing falls as the quantity rises.
                        </p>
                      </>
                    )
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-6">
                  {user ? (
                    <UserMenu
                      name={user.name}
                      image={user.image}
                      updatedAt={user.updatedAt}
                      role={user.role}
                    />
                  ) : (
                    <Link to="/sign-in" className="flex items-center space-x-1">
                      <UserIcon />
                      <span>Sign In</span>
                    </Link>
                  )}
                  {user && (
                    <button
                      type="button"
                      name="Cart"
                      aria-label={`${totalQuantity} items in cart`}
                      className="relative flex items-center rounded bg-opacity-20 p-1"
                      onClick={() => setShowCart(!showCart)}
                    >
                      <span className="mr-2">My Cart</span>
                      <ShoppingBagIcon />
                      {totalQuantity ? (
                        <div className="absolute -right-2 -top-0 h-6 w-6 rounded-full bg-primary-600 text-white">
                          {totalQuantity}
                        </div>
                      ) : (
                        ""
                      )}
                    </button>
                  )}
                  {user && (
                    <button
                      type="button"
                      onClick={async () => {
                        await signOut();
                        // Full reload clears every cached query, so no other
                        // user's data lingers.
                        window.location.href = "/";
                      }}
                    >
                      <div className="flex items-center cursor-pointer">
                        <span className="mr-2">Logout</span>
                        <LogoutIcon />
                      </div>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
      {viewingCustomer && (
        <div
          role="status"
          className="border-b border-amber-300 bg-amber-100 px-4 py-2 text-sm text-amber-900 sm:px-6 lg:px-8"
        >
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2">
            <span>
              You are viewing <strong>{viewingCustomer.name}</strong>'s account as an administrator.
            </span>
            <Link
              to="/account"
              className="rounded-lg bg-amber-900 px-3 py-1 font-semibold text-white hover:bg-amber-800"
            >
              Exit to My Account
            </Link>
          </div>
        </div>
      )}
      {lockNotice && (
        <div
          role="alert"
          className="border-b border-rose-300 bg-rose-100 px-4 py-2 text-sm text-rose-900 sm:px-6 lg:px-8"
        >
          <div className="mx-auto max-w-7xl">{withPhoneLink(lockNotice)}</div>
        </div>
      )}
      <Cart user={user} />
    </div>
  );
}

function unitCentsFor(tiers: PriceTierRow[], productId: string | null, qty: number): number {
  let chosen: PriceTierRow | undefined;
  for (const t of tiers
    .filter((t) => t.product_id === productId)
    .sort((a, b) => a.min_qty - b.min_qty)) {
    if (qty >= t.min_qty) chosen = t;
  }
  return chosen?.unit_cents ?? 0;
}

function Cart({ user }: { user: SessionUser | null }) {
  const { showCart, setShowCart } = useShell();
  const navigate = useNavigate();
  const location = useLocation();
  const cart = useCartCampaigns(user);
  const queryClient = useQueryClient();
  const { data: catalog } = useQuery({
    queryKey: ["catalog"],
    enabled: showCart,
    queryFn: () => api.get<{ products: Product[]; tiers: PriceTierRow[] }>("/catalog/products"),
  });
  const isInEditableUrl = !location.pathname.startsWith("/checkout");

  if (!showCart) return null;
  const lines = cart.data ?? [];
  const subtotal = lines.reduce(
    (sum, c) =>
      sum +
      (c.address_count * unitCentsFor(catalog?.tiers ?? [], c.product_id, c.address_count)) / 100,
    0,
  );

  const checkout = (campaignId: string) => {
    setShowCart(false);
    navigate({ to: "/checkout/$campaignId", params: { campaignId } });
  };
  const remove = async (campaignId: string) => {
    await api.patch(`/campaigns/${campaignId}`, { inCart: false });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["campaigns"] }),
      queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] }),
    ]);
  };

  return (
    <div className="fixed inset-0 z-20 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0 bg-gray-900/40"
          onClick={() => setShowCart(false)}
          onKeyDown={() => {}}
        />
        <div className="fixed inset-y-0 right-0 flex max-w-full pl-10">
          <div className="w-screen max-w-md">
            <div className="flex h-full flex-col bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
                  Your cart
                  {lines.length > 0 && (
                    <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-50 px-1.5 text-xs font-semibold text-primary-700">
                      {lines.length}
                    </span>
                  )}
                </h2>
                <button
                  type="button"
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  onClick={() => setShowCart(false)}
                >
                  <span className="sr-only">Close panel</span>
                  <CloseIcon />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                {lines.length === 0 ? (
                  <EmptyState
                    Icon={ShoppingBagIcon}
                    title="Your cart is empty"
                    message="Campaigns you add to your cart show up here."
                    action={
                      <Link
                        to="/"
                        className="inline-flex items-center rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700"
                        onClick={() => setShowCart(false)}
                      >
                        Browse Products
                      </Link>
                    }
                  />
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {lines.map((c) => {
                      const product = catalog?.products.find((p) => p.id === c.product_id);
                      const unit = unitCentsFor(
                        catalog?.tiers ?? [],
                        c.product_id,
                        c.address_count,
                      );
                      return (
                        <li key={c.id} className="flex gap-4 px-5 py-4">
                          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 p-1.5">
                            {product?.image && (
                              <img
                                width={64}
                                height={64}
                                className="max-h-full max-w-full object-contain"
                                src={product.image}
                                alt=""
                              />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <Link
                                  to="/app/$customerId/mycampaigns/$campaignId"
                                  params={{
                                    customerId: c.customer_id ?? user?.id ?? "",
                                    campaignId: c.id,
                                  }}
                                  className="block truncate text-sm font-semibold text-gray-900 hover:text-primary-600"
                                  onClick={() => setShowCart(false)}
                                >
                                  {c.name}
                                </Link>
                                <Link
                                  to="/"
                                  className="block truncate text-xs text-gray-500 hover:text-gray-700"
                                  onClick={() => setShowCart(false)}
                                >
                                  {product?.name ?? "Product"}
                                </Link>
                              </div>
                              <span className="shrink-0 text-sm font-semibold tabular-nums text-gray-900">
                                ${formatPrices((c.address_count * unit) / 100)}
                              </span>
                            </div>
                            <div className="mt-2 flex items-center justify-between gap-2 text-xs text-gray-500">
                              <span className="flex items-center gap-2">
                                {formatNumbers(c.address_count)} pieces
                                {c.in_cart_at && c.updated_at > c.in_cart_at && (
                                  <span
                                    className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800"
                                    title="Changed since it was added to the cart"
                                  >
                                    Updated
                                  </span>
                                )}
                              </span>
                              {isInEditableUrl && (
                                <span className="flex items-center gap-3">
                                  <button
                                    type="button"
                                    className="font-medium text-gray-500 hover:text-rose-600"
                                    onClick={() => void remove(c.id)}
                                  >
                                    Remove
                                  </button>
                                  <button
                                    type="button"
                                    className="font-medium text-primary-600 hover:text-primary-500"
                                    onClick={() => checkout(c.id)}
                                  >
                                    Checkout
                                  </button>
                                </span>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {lines.length > 0 && isInEditableUrl && (
                <div className="border-t border-gray-100 px-5 py-5">
                  <div className="flex items-baseline justify-between">
                    <p className="text-sm text-gray-500">Subtotal</p>
                    <p className="text-base font-semibold tabular-nums text-gray-900">
                      ${formatPrices(subtotal)}
                    </p>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Postage and GST will be calculated at checkout.
                  </p>
                  <button
                    type="button"
                    className="mt-4 flex w-full items-center justify-center rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-700"
                    onClick={() => checkout(lines[0]!.id)}
                  >
                    Checkout
                  </button>
                  <Link
                    to="/"
                    className="mt-2 flex w-full items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50"
                    onClick={() => setShowCart(false)}
                  >
                    Continue Shopping
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
